import React, { useState, useMemo, useEffect } from "react";
import { 
  Gem, Plus, Trash2, Check, RefreshCw, AlertCircle, Sparkles, PlusCircle, 
  Send, FileText, Edit, CheckCircle2, Clock, ArrowRight, X, LayoutGrid, ShoppingBag
} from "lucide-react";
import { pb } from "../services/pocketbase";
import { suppliesService } from "../services/suppliesService";

export default function ZirconiaSubTab({ supplies, onRefreshSupplies, editSupply, addSupply, removeSupply }) {
  // Obtém cotação do dólar
  const getDolarQuote = () => {
    try {
      const cached = localStorage.getItem("metal_quotes_cache");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.data && parsed.data.usd && parsed.data.usd.price) {
          return Number(parsed.data.usd.price);
        }
      }
    } catch (e) {}
    return 5.36; // Fallback
  };
  const currentDolar = getDolarQuote();

  // Cores padrão iniciais do sistema
  const defaultColors = [
    "Branca",
    "Azul",
    "Verde",
    "Vermelho",
    "Azul Claro",
    "Rosa Turmalina",
    "Roxo",
    "Verde Turmalina",
    "Amarelo",
    "Granada-Zirconia"
  ];

  // Abas internas: 'planilha' ou 'pedidos'
  const [activeSection, setActiveSection] = useState("planilha");

  // Estado das cores disponíveis (dinâmicas + padrões)
  const [colors, setColors] = useState(defaultColors);
  const [activeColor, setActiveColor] = useState("Branca");
  const [newColorInput, setNewColorInput] = useState("");
  const [showAddColor, setShowAddColor] = useState(false);

  // Registro de Configuração Global no DB
  const [configRecord, setConfigRecord] = useState(null);

  // Média Pote local por Cor (Sincronizado com DB e localStorage)
  const [mediaPoteMap, setMediaPoteMap] = useState({});
  const [mediaPoteForm, setMediaPoteForm] = useState(""); // Input de visualização/edição

  // Estado das Compras/Reposições
  const [purchases, setPurchases] = useState([]);
  const [isLoadingPurchases, setIsLoadingPurchases] = useState(false);

  // Modais e Estados de Seleção
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Mapa de quantidades editadas nos modais: { [supplyId]: number }
  const [editedQuantities, setEditedQuantities] = useState({});

  // Status de salvamento inline na planilha
  const [savingStatus, setSavingStatus] = useState({}); // { [stoneId]: 'saving' | 'saved' }

  // Local state buffers for inline input editing to prevent keystroke delay
  const [localInputs, setLocalInputs] = useState({});

  // Formulário de Nova Medida
  const [newSizeForm, setNewSizeForm] = useState({
    size: "",
    peso_total_ct: "",
    peso_medio_pedra: "",
    minimo_estimado: "",
    custo: "" // Novo campo de custo
  });

  // Filtra insumos que pertencem ao setor "pedras"
  const stones = useMemo(() => {
    return supplies.filter(s => s.sector === "pedras");
  }, [supplies]);

  // Conjunto de cores de pedras cadastradas no estoque (em uso)
  const colorsInUse = useMemo(() => {
    const inUse = new Set();
    stones.forEach((s) => {
      const props = s.stone_properties;
      if (props && props.color) {
        inUse.add(props.color.toLowerCase().trim());
      }
    });
    return inUse;
  }, [stones]);

  // Mapeia quais cores possuem pedras abaixo do estoque mínimo e a respectiva quantidade de itens faltantes
  const colorsNeedingReplenishmentMap = useMemo(() => {
    const map = {};
    stones.forEach((s) => {
      const props = s.stone_properties;
      if (!props || !props.is_stone) return;

      const totalWeight = Number(s.current_quantity) || 0;
      const avgWeight = Number(props.peso_medio_pedra) || 0;
      const minUnits = Number(props.minimo_estimado) || 0;
      const mediaPote = props.media_pote !== undefined ? Number(props.media_pote) : (mediaPoteMap[props.color?.toLowerCase()] || 0);

      const pesoMinimo = mediaPote + (minUnits * avgWeight);
      if (totalWeight < pesoMinimo) {
        if (props.color) {
          const key = props.color.toLowerCase().trim();
          map[key] = (map[key] || 0) + 1;
        }
      }
    });
    return map;
  }, [stones, mediaPoteMap]);

  // Calcula o valor estimado de um pedido de reposição
  const getOrderEstimatedValue = (order) => {
    const items = order.items || [];
    return items.reduce((acc, item) => acc + ((item.qty_ordered || 0) * (item.custo_unitario || 0)), 0);
  };

  // Carrega configurações globais (Média Pote e Cores Customizadas)
  const loadZirconiaConfig = async () => {
    try {
      const records = await pb.collection("supplies").getFullList({
        filter: 'name = "__zirconia_config__"',
      });
      
      let potes = {};
      let customColors = [];
      let removedDefaults = [];
      let record = null;

      if (records.length > 0) {
        record = records[0];
        setConfigRecord(record);
        let props = record.stone_properties;
        if (typeof props === "string") {
          try { props = JSON.parse(props); } catch (e) {}
        }
        potes = props?.media_potes || {};
        customColors = props?.custom_colors || [];
        removedDefaults = props?.removed_defaults || [];
      } else {
        // Cria registro de configuração se não existir
        record = await pb.collection("supplies").create({
          name: "__zirconia_config__",
          sector: "config",
          category: "zircônia",
          unit: "none",
          min_quantity: 0,
          current_quantity: 0,
          stone_properties: {
            is_config: true,
            media_potes: {},
            custom_colors: [],
            removed_defaults: []
          }
        });
        setConfigRecord(record);
      }

      setMediaPoteMap(potes);
      if (potes[activeColor.toLowerCase()] !== undefined) {
        setMediaPoteForm(String(potes[activeColor.toLowerCase()]));
      }

      // Filtra as cores padrões removendo as excluídas e unindo com as customizadas
      const filteredDefaults = defaultColors.filter(c => !removedDefaults.includes(c));
      
      // Adiciona também cores de pedras que já estão no banco para garantir consistência
      const dbColors = [];
      stones.forEach((s) => {
        const props = s.stone_properties;
        if (props && props.color) {
          const c = props.color.trim();
          const capitalized = c.charAt(0).toUpperCase() + c.slice(1);
          if (!dbColors.includes(capitalized) && !removedDefaults.includes(capitalized)) {
            dbColors.push(capitalized);
          }
        }
      });

      const mergedColors = [...filteredDefaults, ...customColors];
      dbColors.forEach(c => {
        if (!mergedColors.includes(c)) mergedColors.push(c);
      });

      setColors(mergedColors);
      
      // Se a cor ativa atual foi removida de alguma forma, troca para a primeira disponível
      if (mergedColors.length > 0 && !mergedColors.includes(activeColor)) {
        setActiveColor(mergedColors[0]);
      }
    } catch (e) {
      console.error("Erro ao carregar configuração de zircônias:", e);
    }
  };

  const fetchZirconiaPurchases = async () => {
    setIsLoadingPurchases(true);
    try {
      const records = await pb.collection("supply_purchases").getFullList({
        sort: "-created",
      });
      const filtered = records.filter((r) => {
        let meta = r.metadata;
        if (typeof meta === "string") {
          try { meta = JSON.parse(meta); } catch (e) {}
        }
        return meta && meta.is_zirconia === true;
      });
      setPurchases(filtered);
    } catch (err) {
      console.error("Erro ao buscar pedidos de reposição:", err);
    } finally {
      setIsLoadingPurchases(false);
    }
  };

  useEffect(() => {
    loadZirconiaConfig();
    fetchZirconiaPurchases();
  }, []);

  // Atualiza o input de Média Pote quando a cor muda
  useEffect(() => {
    const val = mediaPoteMap[activeColor.toLowerCase()];
    setMediaPoteForm(val !== undefined ? String(val) : "");
  }, [activeColor, mediaPoteMap]);

  // Filtra as pedras da cor ativa
  const activeColorStones = useMemo(() => {
    return stones.filter((s) => {
      const props = s.stone_properties;
      return props && props.color?.toLowerCase() === activeColor.toLowerCase();
    });
  }, [stones, activeColor]);

  // Média Pote da cor ativa
  const activeMediaPote = useMemo(() => {
    return mediaPoteMap[activeColor.toLowerCase()] || 0;
  }, [activeColor, mediaPoteMap]);

  // Salva o valor de Média Pote da cor de forma persistente
  const handleSaveMediaPote = async (e) => {
    if (e) e.preventDefault();
    const newVal = Number(mediaPoteForm) || 0;
    const updatedMap = { ...mediaPoteMap, [activeColor.toLowerCase()]: newVal };
    
    setMediaPoteMap(updatedMap);
    localStorage.setItem("zirconia_media_pote_map", JSON.stringify(updatedMap));

    try {
      // 1. Salva no registro de configuração global
      if (configRecord) {
        let props = configRecord.stone_properties;
        if (typeof props === "string") {
          try { props = JSON.parse(props); } catch (e) {}
        }
        await pb.collection("supplies").update(configRecord.id, {
          stone_properties: {
            ...props,
            is_config: true,
            media_potes: updatedMap
          }
        });
      }

      // 2. Atualiza a min_quantity (Peso Mínimo) de todas as pedras dessa cor no banco
      if (activeColorStones.length > 0) {
        const promises = activeColorStones.map(async (s) => {
          const props = s.stone_properties || {};
          const updatedProps = {
            ...props,
            media_pote: newVal
          };
          const avgWeight = Number(props?.peso_medio_pedra) || 0;
          const minUnits = Number(props?.minimo_estimado) || 0;
          const newMinQty = newVal + (minUnits * avgWeight);

          await editSupply(s.id, {
            name: s.name,
            sector: s.sector,
            category: s.category,
            unit: s.unit,
            min_quantity: newMinQty,
            current_quantity: s.current_quantity,
            stone_properties: updatedProps
          });
        });

        await Promise.all(promises);
      }

      if (onRefreshSupplies) await onRefreshSupplies();
      alert(`Média Pote para a cor ${activeColor} salva com sucesso!`);
    } catch (err) {
      alert("Erro ao salvar Média Pote: " + err.message);
    }
  };

  const getInputValue = (stone, fieldName) => {
    const key = `${stone.id}_${fieldName}`;
    if (localInputs[key] !== undefined) {
      return localInputs[key];
    }
    if (fieldName === "peso_total_ct") {
      return stone.current_quantity === 0 ? "" : stone.current_quantity;
    }
    const props = stone.stone_properties || {};
    if (fieldName === "custo") {
      return props.custo !== undefined ? props.custo : "";
    }
    return props[fieldName] !== undefined ? props[fieldName] : "";
  };

  const handleInputChange = (stoneId, fieldName, value) => {
    setLocalInputs(prev => ({
      ...prev,
      [`${stoneId}_${fieldName}`]: value
    }));
  };

  const handleInputBlur = async (stone, fieldName) => {
    const key = `${stone.id}_${fieldName}`;
    const value = localInputs[key];
    if (value === undefined) return;

    await handleUpdateField(stone, fieldName, value);

    setLocalInputs(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const handleKeyDown = (e, stone, fieldName) => {
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  // Função para salvar alterações inline de um insumo/pedra
  const handleUpdateField = async (stone, fieldName, value) => {
    setSavingStatus(prev => ({ ...prev, [stone.id]: 'saving' }));
    
    const props = stone.stone_properties || {};
    const updatedProps = { ...props };
    let currentQty = stone.current_quantity;
    let minQty = stone.min_quantity;

    if (fieldName === "peso_total_ct") {
      currentQty = Number(value) || 0;
      updatedProps.last_weight_update = new Date().toISOString(); // Seta a data de alteração de peso
    } else {
      updatedProps[fieldName] = Number(value) || 0;
    }

    // Recalcula o peso mínimo
    const mediaPote = updatedProps.media_pote !== undefined ? Number(updatedProps.media_pote) : activeMediaPote;
    const avgWeight = Number(updatedProps.peso_medio_pedra) || 0;
    const minUnits = Number(updatedProps.minimo_estimado) || 0;
    minQty = mediaPote + (minUnits * avgWeight);

    try {
      await editSupply(stone.id, {
        name: stone.name,
        sector: stone.sector,
        category: stone.category,
        unit: stone.unit,
        min_quantity: minQty,
        current_quantity: currentQty,
        stone_properties: updatedProps
      });
      
      setSavingStatus(prev => ({ ...prev, [stone.id]: 'saved' }));
      setTimeout(() => {
        setSavingStatus(prev => ({ ...prev, [stone.id]: null }));
      }, 1500);

      if (onRefreshSupplies) await onRefreshSupplies();
    } catch (err) {
      console.error(err);
      setSavingStatus(prev => ({ ...prev, [stone.id]: null }));
      alert("Erro ao salvar alteração: " + err.message);
    }
  };

  // Cadastra uma nova Cor
  const handleAddColor = async (e) => {
    e.preventDefault();
    const cleanColor = newColorInput.trim();
    if (!cleanColor) return;

    const capitalized = cleanColor.charAt(0).toUpperCase() + cleanColor.slice(1);
    
    // Verifica se já não existe
    if (colors.includes(capitalized)) {
      setActiveColor(capitalized);
      setNewColorInput("");
      setShowAddColor(false);
      return;
    }

    try {
      let props = configRecord?.stone_properties;
      if (typeof props === "string") {
        try { props = JSON.parse(props); } catch (e) {}
      }

      const customColors = props?.custom_colors || [];
      const removedDefaults = props?.removed_defaults || [];

      // Se for uma cor que estava na lista de removidos padrões, reativa ela
      const updatedRemoved = removedDefaults.filter(c => c !== capitalized);
      const updatedCustom = [...customColors];
      
      if (!defaultColors.includes(capitalized) && !updatedCustom.includes(capitalized)) {
        updatedCustom.push(capitalized);
      }

      const updatedProps = {
        ...props,
        is_config: true,
        custom_colors: updatedCustom,
        removed_defaults: updatedRemoved
      };

      if (configRecord) {
        await pb.collection("supplies").update(configRecord.id, {
          stone_properties: updatedProps
        });
      }

      setColors(prev => {
        const next = [...prev];
        if (!next.includes(capitalized)) next.push(capitalized);
        return next;
      });
      setActiveColor(capitalized);
      setNewColorInput("");
      setShowAddColor(false);
    } catch (err) {
      alert("Erro ao adicionar cor: " + err.message);
    }
  };

  // --- LÓGICA DE EXCLUSÃO DE UMA COR NÃO EM USO ---
  const handleDeleteColor = async (colorName) => {
    // 1. Verifica se há pedras registradas para essa cor
    const hasStones = stones.some((s) => {
      const props = s.stone_properties;
      return props && props.color?.toLowerCase() === colorName.toLowerCase();
    });

    if (hasStones) {
      alert(`Não é possível excluir a cor "${colorName}" porque existem pedras cadastradas nela. Remova todas as medidas desta cor primeiro.`);
      return;
    }

    if (!window.confirm(`Deseja remover a cor "${colorName}" da lista?`)) {
      return;
    }

    try {
      let props = configRecord?.stone_properties;
      if (typeof props === "string") {
        try { props = JSON.parse(props); } catch (e) {}
      }

      const customColors = props?.custom_colors || [];
      const removedDefaults = props?.removed_defaults || [];

      const updatedCustom = customColors.filter(c => c.toLowerCase() !== colorName.toLowerCase());
      const updatedRemoved = [...removedDefaults];

      // Se for uma das cores padrão, registra no array de removidas
      if (defaultColors.includes(colorName) && !updatedRemoved.includes(colorName)) {
        updatedRemoved.push(colorName);
      }

      const updatedProps = {
        ...props,
        is_config: true,
        custom_colors: updatedCustom,
        removed_defaults: updatedRemoved
      };

      if (configRecord) {
        await pb.collection("supplies").update(configRecord.id, {
          stone_properties: updatedProps
        });
      }

      // Atualiza estado local
      const updatedColors = colors.filter(c => c !== colorName);
      setColors(updatedColors);
      
      // Muda a aba ativa
      if (updatedColors.length > 0) {
        setActiveColor(updatedColors[0]);
      }

      alert(`Cor "${colorName}" removida com sucesso!`);
    } catch (err) {
      alert("Erro ao remover cor: " + err.message);
    }
  };

  // Cadastra uma nova Medida/Tamanho
  const handleAddSize = async (e) => {
    e.preventDefault();
    const { size, peso_total_ct, peso_medio_pedra, minimo_estimado, custo } = newSizeForm;
    
    if (!size.trim() || !peso_medio_pedra || !minimo_estimado) {
      alert("Por favor, preencha os campos obrigatórios (Medida, Peso Médio e Mínimo).");
      return;
    }

    const valPesoTotal = Number(peso_total_ct) || 0;
    const valPesoMedio = Number(peso_medio_pedra) || 0;
    const valMinEstimado = Number(minimo_estimado) || 0;
    const valCusto = Number(custo) || 0;

    const name = `Zircônia ${activeColor} - ${size.trim()}`;
    const mediaPote = activeMediaPote;
    const minQty = mediaPote + (valMinEstimado * valPesoMedio);

    const payload = {
      name,
      sector: "pedras",
      category: "zircônia",
      unit: "ct",
      min_quantity: minQty,
      current_quantity: valPesoTotal,
      stone_properties: {
        is_stone: true,
        color: activeColor,
        size: size.trim(),
        media_pote: mediaPote,
        peso_medio_pedra: valPesoMedio,
        minimo_estimado: valMinEstimado,
        custo: valCusto,
        last_weight_update: new Date().toISOString()
      }
    };

    try {
      await addSupply(payload);
      setNewSizeForm({
        size: "",
        peso_total_ct: "",
        peso_medio_pedra: "",
        minimo_estimado: "",
        custo: ""
      });
      if (onRefreshSupplies) await onRefreshSupplies();
    } catch (err) {
      alert("Erro ao cadastrar medida: " + err.message);
    }
  };

  // Deleta um tamanho
  const handleDeleteSize = async (id, sizeName) => {
    if (!window.confirm(`Tem certeza que deseja excluir a Zircônia ${activeColor} no tamanho "${sizeName}"?`)) return;
    try {
      await removeSupply(id);
      if (onRefreshSupplies) await onRefreshSupplies();
    } catch (err) {
      alert("Erro ao excluir: " + err.message);
    }
  };

  // --- LÓGICA DE GERAÇÃO DE PEDIDO DE REPOSIÇÃO ---
  const handleGenerateOrder = async (percent) => {
    const itemsToOrder = [];
    
    stones.forEach((s) => {
      const props = s.stone_properties;
      if (!props || !props.is_stone) return;

      const totalWeight = Number(s.current_quantity) || 0;
      const avgWeight = Number(props.peso_medio_pedra) || 0;
      const minUnits = Number(props.minimo_estimado) || 0;
      const mediaPote = props.media_pote !== undefined ? Number(props.media_pote) : (mediaPoteMap[props.color?.toLowerCase()] || 0);

      const pesoMinimo = mediaPote + (minUnits * avgWeight);
      
      if (totalWeight < pesoMinimo && avgWeight > 0) {
        const missingUnits = minUnits - (totalWeight - mediaPote) / avgWeight;
        if (missingUnits > 0) {
          const suggested = Math.ceil(missingUnits * percent);
          if (suggested > 0) {
            itemsToOrder.push({
              supply_id: s.id,
              name: s.name,
              color: props.color || "",
              size: props.size || "",
              peso_medio_pedra: avgWeight,
              media_pote: mediaPote,
              qty_ordered: suggested,
              qty_received: 0,
              custo_unitario: Number(props.custo) || 0
            });
          }
        }
      }
    });

    if (itemsToOrder.length === 0) {
      alert("Nenhuma pedra com estoque baixo nesta proporção!");
      setShowGenerateModal(false);
      return;
    }

    try {
      const orderType = percent === 1 ? "completo" : percent === 0.5 ? "parcial" : "emergencial";
      const payload = {
        status: "pending", // Pedido Pendente
        items: itemsToOrder,
        metadata: {
          is_zirconia: true,
          order_type: orderType,
          date_ordered: new Date().toISOString()
        }
      };

      await suppliesService.savePurchase(null, payload);
      setShowGenerateModal(false);
      await fetchZirconiaPurchases();
      alert(`Pedido de reposição (${orderType.toUpperCase()}) gerado!`);
    } catch (err) {
      alert("Erro ao criar pedido: " + err.message);
    }
  };

  // Transiciona o status para "Pedido Feito"
  const handleMarkAsOrdered = async (order) => {
    try {
      let meta = order.metadata;
      if (typeof meta === "string") {
        try { meta = JSON.parse(meta); } catch (e) {}
      }
      const updatedMeta = {
        ...meta,
        date_ordered: new Date().toISOString()
      };
      await suppliesService.savePurchase(order.id, {
        status: "ordered",
        metadata: updatedMeta
      });
      await fetchZirconiaPurchases();
      alert("Pedido marcado como Enviado/Feito!");
    } catch (err) {
      alert("Erro ao atualizar pedido: " + err.message);
    }
  };

  // Abre modal de edição do pedido
  const openEditModal = (order) => {
    setSelectedOrder(order);
    const qtys = {};
    order.items.forEach(item => {
      qtys[item.supply_id] = item.qty_ordered;
    });
    setEditedQuantities(qtys);
    setShowEditModal(true);
  };

  const handleSaveOrderEdits = async () => {
    if (!selectedOrder) return;
    try {
      const updatedItems = selectedOrder.items.map(item => ({
        ...item,
        qty_ordered: Number(editedQuantities[item.supply_id]) || 0
      }));

      await suppliesService.savePurchase(selectedOrder.id, {
        items: updatedItems
      });

      setShowEditModal(false);
      await fetchZirconiaPurchases();
      alert("Quantidades atualizadas com sucesso!");
    } catch (err) {
      alert("Erro ao salvar alterações: " + err.message);
    }
  };

  // Abre modal de conferência / recebimento
  const openReceiveModal = (order) => {
    setSelectedOrder(order);
    const qtys = {};
    order.items.forEach(item => {
      qtys[item.supply_id] = item.qty_ordered; // Inicializa com a quantidade solicitada
    });
    setEditedQuantities(qtys);
    setShowReceiveModal(true);
  };

  const handleConfirmReceived = async () => {
    if (!selectedOrder) return;
    try {
      const items = selectedOrder.items || [];
      
      // Atualiza o estoque físico de cada pedra
      const promises = items.map(async (item) => {
        const receivedQty = Number(editedQuantities[item.supply_id]) || 0;
        if (receivedQty <= 0) return;

        const supply = await pb.collection("supplies").getOne(item.supply_id);
        const currentQty = Number(supply.current_quantity) || 0;
        
        let props = supply.stone_properties;
        if (typeof props === "string") {
          try { props = JSON.parse(props); } catch (e) {}
        }
        
        const avgWeight = Number(props?.peso_medio_pedra) || 0;
        const addedWeight = receivedQty * avgWeight;
        const newWeight = currentQty + addedWeight;

        const mediaPote = props?.media_pote !== undefined ? Number(props.media_pote) : (mediaPoteMap[props?.color?.toLowerCase()] || 0);
        const minUnits = Number(props?.minimo_estimado) || 0;
        const minQty = mediaPote + (minUnits * avgWeight);

        const updatedProps = {
          ...props,
          last_weight_update: new Date().toISOString()
        };

        await editSupply(item.supply_id, {
          name: supply.name,
          sector: supply.sector,
          category: supply.category,
          unit: supply.unit,
          min_quantity: minQty,
          current_quantity: newWeight,
          stone_properties: updatedProps
        });
      });

      await Promise.all(promises);

      // Atualiza o registro da compra
      const updatedItems = items.map(item => ({
        ...item,
        qty_received: Number(editedQuantities[item.supply_id]) || 0
      }));

      let meta = selectedOrder.metadata;
      if (typeof meta === "string") {
        try { meta = JSON.parse(meta); } catch (e) {}
      }
      const updatedMeta = {
        ...meta,
        date_received: new Date().toISOString()
      };

      await suppliesService.savePurchase(selectedOrder.id, {
        status: "received",
        items: updatedItems,
        metadata: updatedMeta
      });

      setShowReceiveModal(false);
      await fetchZirconiaPurchases();
      if (onRefreshSupplies) await onRefreshSupplies();
      alert("Estoque de pedras reconciliado e atualizado!");
    } catch (err) {
      alert("Erro ao concluir o recebimento: " + err.message);
    }
  };

  // Deleta um pedido de reposição
  const handleDeletePurchase = async (id) => {
    if (!window.confirm("Deseja excluir permanentemente este pedido de reposição?")) return;
    try {
      await suppliesService.deletePurchase(id);
      await fetchZirconiaPurchases();
    } catch (err) {
      alert("Erro ao excluir pedido: " + err.message);
    }
  };

  // --- EXPORTAR VIA WHATSAPP (wa.me) ---
  const handleExportWhatsApp = (order) => {
    let meta = order.metadata;
    if (typeof meta === "string") {
      try { meta = JSON.parse(meta); } catch (e) {}
    }
    const orderDate = meta?.date_ordered 
      ? new Date(meta.date_ordered).toLocaleDateString("pt-BR") 
      : new Date().toLocaleDateString("pt-BR");
    const orderType = meta?.order_type || "normal";
    
    let text = `📦 *Pedido de Pedras (${orderType === 'completo' ? 'Mínimo' : orderType.toUpperCase()})*\n${orderDate}\n\n`;

    const items = order.items || [];
    const grouped = {};
    items.forEach(item => {
      let size = item.size || "Outros";
      if (!size.toLowerCase().startsWith("zirc") && !size.toLowerCase().startsWith("vidro")) {
        if (item.color?.toLowerCase() === "vermelho") {
          size = `ZIRC ${size}`;
        }
      }
      if (!grouped[size]) grouped[size] = [];
      grouped[size].push(item);
    });

    Object.keys(grouped).sort().forEach(size => {
      text += `🔹 *${size}*\n`;
      grouped[size].forEach(item => {
        text += `*${item.color === 'Branca' ? 'BRANCA' : item.color}* - Qtd: ${item.qty_ordered || 0}\n`;
      });
      text += `\n`;
    });

    const encodedText = encodeURIComponent(text);
    window.open(`https://api.whatsapp.com/send?text=${encodedText}`, "_blank", "noopener,noreferrer");
  };

  // --- EXPORTAR VIA PDF (jsPDF) ---
  const handleExportPDF = (order) => {
    if (!window.jspdf) {
      alert("Biblioteca PDF ainda carregando. Tente novamente.");
      return;
    }
    
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });

    // Cabeçalho Premium Slate-800
    doc.setFillColor(30, 41, 59);
    doc.rect(0, 0, 210, 35, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text("SEMPRE JOIAS", 15, 15);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Gestão de Insumos - Pedido de Reposição de Pedras", 15, 22);
    
    let meta = order.metadata;
    if (typeof meta === "string") {
      try { meta = JSON.parse(meta); } catch (e) {}
    }
    const orderDate = meta?.date_ordered ? new Date(meta.date_ordered).toLocaleDateString("pt-BR") : new Date().toLocaleDateString("pt-BR");
    const orderType = meta?.order_type || "normal";
    const statusText = order.status === "pending" ? "PENDENTE" : order.status === "ordered" ? "FEITO" : "ENTREGUE";

    doc.text(`Data: ${orderDate}`, 150, 15);
    doc.text(`Tipo: ${orderType.toUpperCase()}`, 150, 20);
    doc.text(`Status: ${statusText}`, 150, 25);

    doc.setTextColor(30, 41, 59);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Detalhamento do Pedido", 15, 48);

    doc.setDrawColor(226, 232, 240);
    doc.line(15, 51, 195, 51);

    const items = order.items || [];
    const grouped = {};
    items.forEach(item => {
      const size = item.size || "Outros";
      if (!grouped[size]) grouped[size] = [];
      grouped[size].push(item);
    });

    let posY = 60;
    doc.setFontSize(10);

    Object.keys(grouped).sort().forEach(size => {
      if (posY > 270) {
        doc.addPage();
        posY = 20;
      }

      doc.setFont("helvetica", "bold");
      doc.setTextColor(37, 99, 235); // Blue-600
      doc.text(`Medida: ${size}`, 15, posY);
      posY += 6;

      doc.setFont("helvetica", "bold");
      doc.setTextColor(100, 116, 139); // Slate-500
      doc.text("Cor da Pedra", 20, posY);
      doc.text("Quantidade Sugerida", 100, posY);
      doc.text("Quantidade Recebida", 150, posY);
      posY += 4;
      doc.line(15, posY, 195, posY);
      posY += 6;

      doc.setFont("helvetica", "normal");
      doc.setTextColor(30, 41, 59);

      grouped[size].forEach(item => {
        if (posY > 280) {
          doc.addPage();
          posY = 20;
        }
        
        doc.text(item.color || "Sem Cor", 20, posY);
        doc.text(`${(item.qty_ordered || 0).toLocaleString()} un`, 100, posY);
        doc.text(order.status === "received" ? `${(item.qty_received || 0).toLocaleString()} un` : "-", 150, posY);
        posY += 6;
      });

      posY += 4;
    });

    const filename = `pedido_zirconia_${orderType}_${orderDate.replace(/\//g, "-")}.pdf`;
    doc.save(filename);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden h-full p-4 lg:p-6 gap-6">
      
      {/* Navegação Principal das sub-abas internas */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm shrink-0 gap-3">
        <div className="flex gap-2 w-full sm:w-auto overflow-x-auto">
          <button
            onClick={() => setActiveSection("planilha")}
            className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 font-bold text-xs rounded-lg transition-colors whitespace-nowrap ${
              activeSection === "planilha" ? "bg-blue-600 text-white shadow" : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            <LayoutGrid size={16} /> Planilha de Pedras
          </button>
          <button
            onClick={() => setActiveSection("pedidos")}
            className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 font-bold text-xs rounded-lg transition-colors whitespace-nowrap ${
              activeSection === "pedidos" ? "bg-blue-600 text-white shadow" : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            <ShoppingBag size={16} /> Pedidos de Reposição
          </button>
        </div>
        
        {activeSection === "pedidos" && (
          <button
            onClick={() => setShowGenerateModal(true)}
            className="w-full sm:w-auto flex items-center justify-center gap-1.5 bg-slate-800 hover:bg-black text-white text-xs font-bold px-3 py-2 rounded-lg shadow transition-all whitespace-nowrap"
          >
            <PlusCircle size={16} /> Gerar Reposição
          </button>
        )}
      </div>

      {/* --- SEÇÃO 1: PLANILHA DE ESTOQUE --- */}
      {activeSection === "planilha" && (
        <div className="flex-1 flex flex-col overflow-hidden gap-6 min-h-0">
          
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shrink-0 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex-1 text-left">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <Gem className="text-blue-600 animate-pulse" /> Tabela Geral de Zircônias
                </h2>
                {Object.keys(colorsNeedingReplenishmentMap).length > 0 && (
                  <span className="flex items-center gap-1 bg-red-50 border border-red-200 text-red-700 text-[10px] font-black uppercase px-2.5 py-1 rounded-full animate-pulse">
                    <AlertCircle size={12} /> Pedras precisam ser repostas!
                  </span>
                )}
                <span className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-black uppercase px-2.5 py-1 rounded-full flex items-center gap-1">
                  Cotação Dólar: R$ {currentDolar.toFixed(2)}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Ajuste os pesos (ct) e custos (US$) inline. As atualizações salvam automaticamente ao clicar fora.
              </p>
            </div>
            <button
              onClick={() => setShowAddColor(!showAddColor)}
              className="w-full sm:w-auto flex items-center justify-center gap-1 bg-slate-800 hover:bg-black text-white text-xs font-bold px-3 py-2 rounded-lg shadow transition-all"
            >
              <PlusCircle size={16} /> Nova Cor
            </button>
          </div>

          {showAddColor && (
            <form onSubmit={handleAddColor} className="flex gap-2 bg-slate-100 p-3 rounded-xl border border-slate-200 max-w-sm shrink-0 animate-in fade-in slide-in-from-top-2 duration-200">
              <input 
                type="text" 
                placeholder="Nome da cor (Ex: Turquesa)..."
                required
                autoFocus
                value={newColorInput}
                onChange={e => setNewColorInput(e.target.value)}
                className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-4 py-1.5 rounded-lg transition-colors">
                Adicionar
              </button>
              <button type="button" onClick={() => setShowAddColor(false)} className="text-slate-500 hover:text-slate-700 text-xs px-2">
                Cancelar
              </button>
            </form>
          )}

          {/* Filtro de Cores Responsivo */}
          {/* Dropdown em telas pequenas (Mobile) */}
          <div className="block md:hidden shrink-0">
            <label className="block text-[10px] font-extrabold text-slate-400 uppercase mb-1 tracking-wider text-left">Selecionar Cor</label>
            <select
              value={activeColor}
              onChange={(e) => setActiveColor(e.target.value)}
              className="w-full p-2.5 text-xs font-bold border border-slate-300 rounded-lg bg-white outline-none focus:border-blue-500 text-slate-700"
            >
              {colors.map((color) => {
                const missingCount = colorsNeedingReplenishmentMap[color.toLowerCase().trim()] || 0;
                const warningText = missingCount > 0 ? ` ⚠️ (${missingCount} reposições)` : "";
                return (
                  <option key={color} value={color}>
                    {color.toUpperCase()}{warningText}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Tabs Horizontais em telas maiores (Desktop/Tablet) */}
          <div className="hidden md:flex gap-2 border-b border-slate-200 pb-1 overflow-x-auto shrink-0 [&::-webkit-scrollbar]:hidden">
            {colors.map((color) => {
              const isActive = color === activeColor;
              const isColorInUse = colorsInUse.has(color.toLowerCase().trim());
              const missingCount = colorsNeedingReplenishmentMap[color.toLowerCase().trim()] || 0;
              return (
                <div
                  key={color}
                  className={`flex items-center border-b-2 transition-all whitespace-nowrap rounded-t-lg ${
                    isActive 
                      ? "border-blue-600 text-blue-600 scale-105" 
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setActiveColor(color)}
                    className="flex items-center gap-2 px-4 py-2 font-bold text-xs uppercase tracking-wider outline-none"
                  >
                    <span className={`w-3 h-3 rounded-full ${getColorCircleClass(color)}`}></span>
                    {color}
                    {missingCount > 0 && (
                      <span className="text-red-600 font-black ml-1 text-xs" title={`${missingCount} pedras abaixo do mínimo!`}>
                        ({missingCount})
                      </span>
                    )}
                  </button>
                  {!isColorInUse && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteColor(color);
                      }}
                      className="p-1 pr-3 pl-0 text-slate-400 hover:text-red-500 transition-colors"
                      title={`Excluir cor ${color}`}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex-1 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col min-h-0">
            {/* Controle da Média Pote desta Cor */}
            <form onSubmit={handleSaveMediaPote} className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-amber-500 animate-spin" style={{ animationDuration: '6s' }} />
                <span className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  Média Pote para a cor <span className="text-blue-600">{activeColor.toUpperCase()}</span>
                  {!colorsInUse.has(activeColor.toLowerCase().trim()) && (
                    <button
                      type="button"
                      onClick={() => handleDeleteColor(activeColor)}
                      className="text-slate-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors"
                      title={`Excluir cor ${activeColor}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </span>
              </div>
              
              <div className="flex items-center gap-2 max-w-[280px]">
                <input 
                  type="number"
                  step="any"
                  min="0"
                  required
                  placeholder="0.00"
                  value={mediaPoteForm}
                  onChange={(e) => setMediaPoteForm(e.target.value)}
                  className="w-full text-right font-bold text-sm bg-white border border-slate-300 rounded-lg px-3 py-1.5 focus:ring-1 focus:ring-blue-500 outline-none"
                />
                <span className="text-xs text-slate-400 font-bold">ct</span>
                
                {mediaPoteForm !== String(activeMediaPote) && (
                  <button 
                    type="submit" 
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors shadow-sm"
                  >
                    Salvar
                  </button>
                )}
              </div>
            </form>

            {/* Tabela de Medidas */}
            <div className="flex-1 overflow-y-auto">
              
              {/* Visualização em Tabela (Desktop/Tablet) */}
              <div className="hidden md:block">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-50/50 text-slate-500 text-[10px] font-extrabold uppercase border-b border-slate-200 sticky top-0 z-10">
                    <tr>
                      <th className="p-4 pl-6">Medida / Tamanho</th>
                      <th className="p-4 text-right w-32">Peso Total (ct)</th>
                      <th className="p-4 text-center w-32">Última Pesagem</th>
                      <th className="p-4 text-right w-32">Peso Médio Pedra (ct)</th>
                      <th className="p-4 text-right w-32">Mín. Estimado (un)</th>
                      <th className="p-4 text-right w-32 text-emerald-600">Custo (US$)</th>
                      <th className="p-4 text-right w-32">Peso Mínimo (ct)</th>
                      <th className="p-4 text-right w-32">Qtd. Pedido (un)</th>
                      <th className="p-4 text-center w-24">Status</th>
                      <th className="p-4 text-center w-16">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {activeColorStones.map((stone) => {
                      const props = stone.stone_properties || {};

                      const totalWeight = Number(stone.current_quantity) || 0;
                      const avgWeight = Number(props?.peso_medio_pedra) || 0;
                      const minUnits = Number(props?.minimo_estimado) || 0;
                      const mediaPote = props?.media_pote !== undefined ? Number(props.media_pote) : activeMediaPote;

                      // Cálculos Automáticos
                      const pesoMinimo = mediaPote + (minUnits * avgWeight);
                      const isBelowMin = totalWeight < pesoMinimo;
                      const qtyPedido = isBelowMin && avgWeight > 0
                        ? Math.ceil(minUnits - (totalWeight - mediaPote) / avgWeight)
                        : 0;

                      const lastUpdate = props?.last_weight_update 
                        ? new Date(props.last_weight_update).toLocaleDateString("pt-BR") 
                        : "-";

                      const isSaving = savingStatus[stone.id] === 'saving';
                      const isSaved = savingStatus[stone.id] === 'saved';

                      return (
                        <tr key={stone.id} className="hover:bg-blue-50/10 transition-colors">
                          <td className="p-4 pl-6 font-bold text-slate-800">
                            <div className="flex items-center gap-2">
                              {props?.size || stone.name.split(" - ")[1]?.trim() || stone.name}
                              {isSaving && <span className="w-3 h-3 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></span>}
                              {isSaved && <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded font-black animate-fade-out">Salvo!</span>}
                            </div>
                          </td>

                          {/* Peso Total (Editável) */}
                          <td className="p-4 text-right">
                             <input 
                               type="number"
                               step="any"
                               min="0"
                               value={getInputValue(stone, "peso_total_ct")}
                               onChange={(e) => handleInputChange(stone.id, "peso_total_ct", e.target.value)}
                               onBlur={() => handleInputBlur(stone, "peso_total_ct")}
                               onKeyDown={(e) => handleKeyDown(e, stone, "peso_total_ct")}
                               placeholder="0.00"
                               className="w-full text-right font-mono font-bold text-blue-600 bg-slate-50 focus:bg-white border border-transparent hover:border-slate-200 focus:border-blue-500 rounded px-2 py-1 outline-none"
                             />
                           </td>

                           {/* Última Pesagem */}
                           <td className="p-4 text-center font-mono text-xs text-slate-500">
                             {lastUpdate}
                           </td>

                           {/* Peso Médio Pedra (Editável) */}
                           <td className="p-4 text-right">
                             <input 
                               type="number"
                               step="any"
                               min="0"
                               value={getInputValue(stone, "peso_medio_pedra")}
                               onChange={(e) => handleInputChange(stone.id, "peso_medio_pedra", e.target.value)}
                               onBlur={() => handleInputBlur(stone, "peso_medio_pedra")}
                               onKeyDown={(e) => handleKeyDown(e, stone, "peso_medio_pedra")}
                               placeholder="0.0000"
                               className="w-full text-right font-mono text-slate-700 bg-slate-50 focus:bg-white border border-transparent hover:border-slate-200 focus:border-blue-500 rounded px-2 py-1 outline-none"
                             />
                           </td>

                           {/* Mínimo Estimado (Editável) */}
                           <td className="p-4 text-right">
                             <input 
                               type="number"
                               min="0"
                               value={getInputValue(stone, "minimo_estimado")}
                               onChange={(e) => handleInputChange(stone.id, "minimo_estimado", e.target.value)}
                               onBlur={() => handleInputBlur(stone, "minimo_estimado")}
                               onKeyDown={(e) => handleKeyDown(e, stone, "minimo_estimado")}
                               placeholder="0"
                               className="w-full text-right font-mono text-slate-700 bg-slate-50 focus:bg-white border border-transparent hover:border-slate-200 focus:border-blue-500 rounded px-2 py-1 outline-none"
                             />
                           </td>

                           {/* Custo Unitário (Editável) */}
                           <td className="p-4 text-right">
                              <div className="flex flex-col items-end">
                               <input 
                                 type="number"
                                 step="any"
                                 min="0"
                                 value={getInputValue(stone, "custo")}
                                 onChange={(e) => handleInputChange(stone.id, "custo", e.target.value)}
                                 onBlur={() => handleInputBlur(stone, "custo")}
                                 onKeyDown={(e) => handleKeyDown(e, stone, "custo")}
                                 placeholder="0.00"
                                 className="w-full text-right font-mono font-bold text-emerald-600 bg-slate-50 focus:bg-white border border-transparent hover:border-slate-200 focus:border-blue-500 rounded px-2 py-1 outline-none"
                               />
                               <span className="text-[10px] text-slate-400 mt-0.5 font-mono">
                                 R$ {((Number(getInputValue(stone, "custo")) || 0) * currentDolar).toFixed(4)}
                               </span>
                              </div>
                            </td>

                          {/* Peso Mínimo (Calculado) */}
                          <td className="p-4 text-right font-mono text-slate-500 font-medium">
                            {pesoMinimo.toFixed(2)} <span className="text-[10px] text-slate-400">ct</span>
                          </td>

                          {/* Qtd Pedido (Calculado) */}
                          <td className={`p-4 text-right font-mono font-bold ${qtyPedido > 0 ? "text-amber-600 bg-amber-50/20" : "text-slate-400"}`}>
                            {qtyPedido > 0 ? qtyPedido.toLocaleString() : "-"}
                          </td>

                          {/* Status (Calculado) */}
                          <td className="p-4 text-center font-mono">
                            {isBelowMin ? (
                              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-red-50 border border-red-100 text-red-600 animate-pulse">
                                <AlertCircle size={8} /> Repor
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-emerald-50 border border-emerald-100 text-emerald-600">
                                OK
                              </span>
                            )}
                          </td>

                          <td className="p-4 text-center">
                            <button
                              onClick={() => handleDeleteSize(stone.id, props?.size || stone.name)}
                              className="p-1 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                              title="Deletar tamanho"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}

                    {activeColorStones.length === 0 && (
                      <tr>
                        <td colSpan="10" className="text-center py-12 text-slate-400">
                          Nenhuma medida cadastrada para a cor {activeColor}. Cadastre a primeira abaixo!
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Visualização em Cards (Mobile) */}
              <div className="block md:hidden p-4 space-y-4">
                {activeColorStones.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 bg-white border border-slate-200 rounded-xl shadow-sm">
                    Nenhuma medida cadastrada para a cor {activeColor}. Cadastre a primeira abaixo!
                  </div>
                ) : (
                  activeColorStones.map((stone) => {
                    const props = stone.stone_properties || {};

                    const totalWeight = Number(stone.current_quantity) || 0;
                    const avgWeight = Number(props?.peso_medio_pedra) || 0;
                    const minUnits = Number(props?.minimo_estimado) || 0;
                    const mediaPote = props?.media_pote !== undefined ? Number(props.media_pote) : activeMediaPote;

                    // Cálculos Automáticos
                    const pesoMinimo = mediaPote + (minUnits * avgWeight);
                    const isBelowMin = totalWeight < pesoMinimo;
                    const qtyPedido = isBelowMin && avgWeight > 0
                      ? Math.ceil(minUnits - (totalWeight - mediaPote) / avgWeight)
                      : 0;

                    const lastUpdate = props?.last_weight_update 
                      ? new Date(props.last_weight_update).toLocaleDateString("pt-BR") 
                      : "-";

                    const isSaving = savingStatus[stone.id] === 'saving';
                    const isSaved = savingStatus[stone.id] === 'saved';

                    return (
                      <div 
                        key={stone.id}
                        className={`p-4 bg-white border rounded-xl shadow-sm flex flex-col gap-3 transition-colors ${
                          isBelowMin ? "border-red-200 bg-red-50/5" : "border-slate-200"
                        }`}
                      >
                        {/* Title & Delete button */}
                        <div className="flex justify-between items-start gap-2">
                          <div className="text-left">
                            <div className="font-bold text-slate-800 text-sm flex items-center gap-2">
                              {props?.size || stone.name.split(" - ")[1]?.trim() || stone.name}
                              {isSaving && <span className="w-3.5 h-3.5 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></span>}
                              {isSaved && <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded font-black animate-fade-out">Salvo!</span>}
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
                              Última Pesagem: {lastUpdate}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            {isBelowMin ? (
                              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[9px] font-black uppercase bg-red-50 border border-red-100 text-red-600 animate-pulse">
                                <AlertCircle size={8} /> Repor
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[9px] font-black uppercase bg-emerald-50 border border-emerald-100 text-emerald-600">
                                OK
                              </span>
                            )}

                            <button
                              onClick={() => handleDeleteSize(stone.id, props?.size || stone.name)}
                              className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                              title="Deletar tamanho"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>

                        {/* Calculated metrics details */}
                        <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 p-2.5 rounded-lg border border-slate-100 font-mono">
                          <div className="text-left">
                            <span className="text-slate-400 block text-[9px] uppercase font-bold">Peso Mínimo</span>
                            <strong className="text-slate-700">{pesoMinimo.toFixed(2)} ct</strong>
                          </div>
                          <div className="text-left">
                            <span className="text-slate-400 block text-[9px] uppercase font-bold">Qtd. Sugerida</span>
                            <strong className={qtyPedido > 0 ? "text-amber-600 font-black" : "text-slate-500"}>
                              {qtyPedido > 0 ? qtyPedido.toLocaleString() + " un" : "-"}
                            </strong>
                          </div>
                        </div>

                        {/* Editable Inputs Grid */}
                        <div className="grid grid-cols-2 gap-3 mt-1">
                          <div className="text-left">
                            <label className="block text-[9px] uppercase font-bold text-slate-400 mb-1">Peso Total (ct)</label>
                            <input 
                              type="number"
                              step="any"
                              min="0"
                              value={getInputValue(stone, "peso_total_ct")}
                              onChange={(e) => handleInputChange(stone.id, "peso_total_ct", e.target.value)}
                              onBlur={() => handleInputBlur(stone, "peso_total_ct")}
                              onKeyDown={(e) => handleKeyDown(e, stone, "peso_total_ct")}
                              placeholder="0.00"
                              className="w-full text-sm font-bold text-blue-600 bg-slate-50 focus:bg-white border border-slate-200 hover:border-slate-300 focus:border-blue-500 rounded-lg px-2.5 py-1.5 outline-none font-mono"
                            />
                          </div>

                          <div className="text-left">
                            <label className="block text-[9px] uppercase font-bold text-slate-400 mb-1">Peso Médio (ct)</label>
                            <input 
                              type="number"
                              step="any"
                              min="0"
                              value={getInputValue(stone, "peso_medio_pedra")}
                              onChange={(e) => handleInputChange(stone.id, "peso_medio_pedra", e.target.value)}
                              onBlur={() => handleInputBlur(stone, "peso_medio_pedra")}
                              onKeyDown={(e) => handleKeyDown(e, stone, "peso_medio_pedra")}
                              placeholder="0.0000"
                              className="w-full text-sm text-slate-700 bg-slate-50 focus:bg-white border border-slate-200 hover:border-slate-300 focus:border-blue-500 rounded-lg px-2.5 py-1.5 outline-none font-mono"
                            />
                          </div>

                          <div className="text-left">
                            <label className="block text-[9px] uppercase font-bold text-slate-400 mb-1">Mín. Estimado (un)</label>
                            <input 
                              type="number"
                              min="0"
                              value={getInputValue(stone, "minimo_estimado")}
                              onChange={(e) => handleInputChange(stone.id, "minimo_estimado", e.target.value)}
                              onBlur={() => handleInputBlur(stone, "minimo_estimado")}
                              onKeyDown={(e) => handleKeyDown(e, stone, "minimo_estimado")}
                              placeholder="0"
                              className="w-full text-sm text-slate-700 bg-slate-50 focus:bg-white border border-slate-200 hover:border-slate-300 focus:border-blue-500 rounded-lg px-2.5 py-1.5 outline-none font-mono"
                            />
                          </div>

                          <div className="text-left">
                            <label className="block text-[9px] uppercase font-bold text-slate-400 mb-1 text-emerald-600">Custo (US$/un)</label>
                            <input 
                              type="number"
                              step="any"
                              min="0"
                              value={getInputValue(stone, "custo")}
                              onChange={(e) => handleInputChange(stone.id, "custo", e.target.value)}
                              onBlur={() => handleInputBlur(stone, "custo")}
                              onKeyDown={(e) => handleKeyDown(e, stone, "custo")}
                              placeholder="0.00"
                              className="w-full text-sm font-bold text-emerald-600 bg-slate-50 focus:bg-white border border-slate-200 hover:border-slate-300 focus:border-emerald-500 rounded-lg px-2.5 py-1.5 outline-none font-mono"
                            />
                            <span className="text-[10px] text-slate-400 mt-1 block font-mono text-right">
                              R$ {((Number(getInputValue(stone, "custo")) || 0) * currentDolar).toFixed(4)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

            </div>

            {/* Adicionar Novo Tamanho (Formulário Inferior) */}
            <div className="bg-slate-50 border-t border-slate-200 p-4 shrink-0">
              <form onSubmit={handleAddSize} className="flex flex-col md:flex-row gap-3 items-end max-w-5xl mx-auto">
                <div className="flex-1 w-full">
                  <label className="block text-[10px] font-extrabold uppercase text-slate-500 mb-1">
                    Nova Medida / Tamanho *
                  </label>
                  <input 
                    type="text" 
                    required
                    placeholder="Ex: Redonda 1.25mm, Navete 2.5x5mm"
                    value={newSizeForm.size}
                    onChange={e => setNewSizeForm({ ...newSizeForm, size: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                
                <div className="w-full md:w-32">
                  <label className="block text-[10px] font-extrabold uppercase text-slate-500 mb-1">
                    Peso Inicial (ct)
                  </label>
                  <input 
                    type="number" 
                    step="any"
                    min="0"
                    placeholder="0.00"
                    value={newSizeForm.peso_total_ct}
                    onChange={e => setNewSizeForm({ ...newSizeForm, peso_total_ct: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div className="w-full md:w-44">
                  <label className="block text-[10px] font-extrabold uppercase text-slate-500 mb-1 text-blue-600">
                    Peso Médio Pedra (ct) *
                  </label>
                  <input 
                    type="number" 
                    step="any"
                    min="0"
                    required
                    placeholder="Ex: 0.0045"
                    value={newSizeForm.peso_medio_pedra}
                    onChange={e => setNewSizeForm({ ...newSizeForm, peso_medio_pedra: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div className="w-full md:w-32">
                  <label className="block text-[10px] font-extrabold uppercase text-slate-500 mb-1 text-red-650 font-bold">
                    Mínimo (un) *
                  </label>
                  <input 
                    type="number" 
                    min="0"
                    required
                    placeholder="Ex: 2000"
                    value={newSizeForm.minimo_estimado}
                    onChange={e => setNewSizeForm({ ...newSizeForm, minimo_estimado: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div className="w-full md:w-36">
                  <label className="block text-[10px] font-extrabold uppercase text-slate-500 mb-1 text-emerald-600 font-bold">
                    Custo (US$/un)
                  </label>
                  <div className="flex flex-col gap-1">
                    <input 
                      type="number" 
                      step="any"
                      min="0"
                      placeholder="Ex: 0.15"
                      value={newSizeForm.custo}
                      onChange={e => setNewSizeForm({ ...newSizeForm, custo: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    {newSizeForm.custo && (
                      <span className="text-[9px] text-slate-400 font-mono">
                        ≈ R$ {((Number(newSizeForm.custo) || 0) * currentDolar).toFixed(4)}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full md:w-auto px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-lg transition-colors flex items-center justify-center gap-1.5 h-[38px] shadow-sm uppercase tracking-wide shrink-0"
                >
                  <Plus size={16} /> Adicionar
                </button>
              </form>
            </div>

          </div>
        </div>
      )}

      {/* --- SEÇÃO 2: PEDIDOS DE REPOSIÇÃO --- */}
      {activeSection === "pedidos" && (
        <div className="flex-1 flex flex-col overflow-hidden gap-4 min-h-0 bg-white rounded-xl border border-slate-200 shadow-sm p-4 lg:p-6">
          <div className="flex justify-between items-center border-b border-slate-100 pb-4 shrink-0">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Histórico de Pedidos de Pedras</h3>
              <p className="text-xs text-slate-400">Consolidação de compras, envios para WhatsApp e recebimentos de zirconias.</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoadingPurchases ? (
              <div className="h-full flex items-center justify-center text-slate-500">
                <RefreshCw size={24} className="animate-spin text-blue-500" />
                <span className="ml-2">Buscando histórico...</span>
              </div>
            ) : purchases.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-400">
                <ShoppingBag size={48} className="stroke-1 opacity-50 mb-3" />
                <p>Nenhum pedido de reposição gerado até o momento.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {purchases.map((order) => {
                  let meta = order.metadata;
                  if (typeof meta === "string") {
                    try { meta = JSON.parse(meta); } catch (e) {}
                  }

                  const orderDate = meta?.date_ordered 
                    ? new Date(meta.date_ordered).toLocaleDateString("pt-BR") 
                    : new Date(order.created).toLocaleDateString("pt-BR");
                  const orderTime = meta?.date_ordered
                    ? new Date(meta.date_ordered).toLocaleTimeString("pt-BR", { hour: '2-digit', minute: '2-digit' })
                    : new Date(order.created).toLocaleTimeString("pt-BR", { hour: '2-digit', minute: '2-digit' });

                  const orderType = meta?.order_type || "normal";
                  const itemsCount = order.items?.length || 0;

                  return (
                    <div 
                      key={order.id}
                      className="bg-slate-50 hover:bg-slate-100/70 border border-slate-200 rounded-xl p-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 transition-all"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full border ${
                            order.status === "pending"
                              ? "bg-amber-50 text-amber-700 border-amber-200"
                              : order.status === "ordered"
                              ? "bg-blue-50 text-blue-700 border-blue-200"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200"
                          }`}>
                            {order.status === "pending" ? "Pendente" : order.status === "ordered" ? "Feito" : "Entregue"}
                          </span>
                          
                          <span className="text-[10px] bg-slate-800 text-white font-extrabold uppercase px-2.5 py-0.5 rounded-full tracking-wide">
                            REPOSIÇÃO {orderType === 'completo' ? 'MÍNIMA (100%)' : orderType === 'parcial' ? 'PARCIAL (50%)' : 'EMERGENCIAL (25%)'}
                          </span>
                        </div>

                         <div className="text-sm font-semibold text-slate-800 pt-1 flex items-center gap-3 flex-wrap">
                           <span>Pedido gerado em: {orderDate} às {orderTime}</span>
                           {getOrderEstimatedValue(order) > 0 && (
                              <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-lg flex items-center gap-1 shrink-0">
                                Valor Est.: US$ {getOrderEstimatedValue(order).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            )}
                         </div>
                        <div className="text-xs text-slate-500">
                          Contém {itemsCount} medidas de zircônias cadastradas para reposição.
                        </div>
                      </div>

                      {/* Ações por status */}
                      <div className="flex gap-2 flex-wrap self-end md:self-auto">
                        <button
                          onClick={() => handleExportPDF(order)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-lg text-xs font-bold shadow-sm transition-colors"
                          title="Exportar para PDF"
                        >
                          <FileText size={14} /> PDF
                        </button>
                        
                        <button
                          onClick={() => handleExportWhatsApp(order)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold shadow-sm transition-colors"
                          title="Enviar pelo WhatsApp"
                        >
                          <Send size={14} /> WhatsApp
                        </button>

                        {order.status === "pending" && (
                          <>
                            <button
                              onClick={() => openEditModal(order)}
                              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-lg text-xs font-bold shadow-sm transition-colors"
                              title="Editar pedido"
                            >
                              <Edit size={14} /> Editar
                            </button>

                            <button
                              onClick={() => handleMarkAsOrdered(order)}
                              className="flex items-center gap-1 px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold shadow-sm transition-all active:scale-95"
                            >
                              Fazer Pedido <ArrowRight size={14} />
                            </button>
                          </>
                        )}

                        {order.status === "ordered" && (
                          <button
                            onClick={() => openReceiveModal(order)}
                            className="flex items-center gap-1 px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold shadow-sm transition-all active:scale-95"
                          >
                            <CheckCircle2 size={14} /> Receber Pedido
                          </button>
                        )}

                        <button
                          onClick={() => handleDeletePurchase(order.id)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Excluir pedido"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- MODAL 1: SELEÇÃO DO TIPO DE PEDIDO A GERAR --- */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-full animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-center p-6 border-b border-slate-100 shrink-0 bg-slate-50 rounded-t-2xl">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Gem className="text-blue-600" /> Gerar Pedido de Reposição
              </h3>
              <button
                onClick={() => setShowGenerateModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-100 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-500 leading-relaxed">
                Selecione o tipo de reposição que deseja gerar. O sistema calculará todas as pedras abaixo do peso mínimo configurado:
              </p>

              <button
                onClick={() => handleGenerateOrder(1)}
                className="w-full text-left p-4 bg-slate-50 hover:bg-blue-50/50 border border-slate-200 hover:border-blue-300 rounded-xl transition-all flex justify-between items-center"
              >
                <div>
                  <h4 className="font-bold text-slate-800 text-sm">Pedido Mínimo Completo (100%)</h4>
                  <p className="text-xs text-slate-400 mt-1">Repõe a quantidade inteira necessária para atingir o mínimo.</p>
                </div>
                <span className="bg-blue-100 text-blue-800 text-[10px] font-black px-2 py-0.5 rounded">100%</span>
              </button>

              <button
                onClick={() => handleGenerateOrder(0.5)}
                className="w-full text-left p-4 bg-slate-50 hover:bg-blue-50/50 border border-slate-200 hover:border-blue-300 rounded-xl transition-all flex justify-between items-center"
              >
                <div>
                  <h4 className="font-bold text-slate-800 text-sm">Pedido Parcial (50%)</h4>
                  <p className="text-xs text-slate-400 mt-1">Repõe metade da quantidade necessária para atingir o mínimo.</p>
                </div>
                <span className="bg-amber-100 text-amber-800 text-[10px] font-black px-2 py-0.5 rounded">50%</span>
              </button>

              <button
                onClick={() => handleGenerateOrder(0.25)}
                className="w-full text-left p-4 bg-slate-50 hover:bg-blue-50/50 border border-slate-200 hover:border-blue-300 rounded-xl transition-all flex justify-between items-center"
              >
                <div>
                  <h4 className="font-bold text-slate-800 text-sm">Pedido Emergencial (25%)</h4>
                  <p className="text-xs text-slate-400 mt-1">Repõe apenas 25% da quantidade necessária (lotes críticos).</p>
                </div>
                <span className="bg-red-100 text-red-800 text-[10px] font-black px-2 py-0.5 rounded">25%</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL 2: EDIÇÃO DE QUANTIDADES (PENDENTE) --- */}
      {showEditModal && selectedOrder && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col max-h-[85vh] animate-in fade-in zoom-in duration-200 overflow-hidden">
            <div className="p-6 border-b border-slate-100 shrink-0 bg-slate-50 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-slate-800">Ajustar Quantidades do Pedido</h3>
                <p className="text-xs text-slate-400">Edite as quantidades de compra de pedras antes de enviá-lo.</p>
              </div>
              <button onClick={() => setShowEditModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-100">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <table className="w-full text-left border-collapse text-xs">
                <thead className="bg-slate-100 sticky top-0 text-slate-500 font-bold border-b border-slate-200">
                  <tr>
                    <th className="p-3">Medida / Tamanho</th>
                    <th className="p-3">Cor</th>
                    <th className="p-3 text-right w-44">Qtd Compra (unidades)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {selectedOrder.items.map((item) => (
                    <tr key={item.supply_id} className="hover:bg-slate-50">
                      <td className="p-3 font-semibold text-slate-700">{item.size}</td>
                      <td className="p-3 uppercase font-bold text-blue-600">{item.color}</td>
                      <td className="p-3 text-right">
                        <input
                          type="number"
                          min="0"
                          value={editedQuantities[item.supply_id] !== undefined ? editedQuantities[item.supply_id] : ""}
                          onChange={(e) => setEditedQuantities({ ...editedQuantities, [item.supply_id]: Number(e.target.value) })}
                          className="w-full text-right font-mono font-bold border border-slate-300 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 shrink-0">
              <button onClick={() => setShowEditModal(false)} className="px-5 py-2 font-bold text-slate-600 hover:bg-slate-200 rounded-lg text-xs">
                Cancelar
              </button>
              <button onClick={handleSaveOrderEdits} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-xs shadow-sm">
                Salvar Alterações
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL 3: CONFERÊNCIA DE RECEBIMENTO (ENTREGUE) --- */}
      {showReceiveModal && selectedOrder && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[85vh] animate-in fade-in zoom-in duration-200 overflow-hidden">
            <div className="p-6 border-b border-slate-100 shrink-0 bg-slate-50 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-1.5">
                  <CheckCircle2 className="text-emerald-600" /> Conferência de Pedras Recebidas
                </h3>
                <p className="text-xs text-slate-400">Verifique e altere as quantidades que chegaram de fato no pacote.</p>
              </div>
              <button onClick={() => setShowReceiveModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-100">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <table className="w-full text-left border-collapse text-xs">
                <thead className="bg-slate-100 sticky top-0 text-slate-500 font-bold border-b border-slate-200">
                  <tr>
                    <th className="p-3">Medida / Tamanho</th>
                    <th className="p-3">Cor</th>
                    <th className="p-3 text-right">Qtd Solicitada</th>
                    <th className="p-3 text-right w-44">Qtd Recebida (unidades)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {selectedOrder.items.map((item) => (
                    <tr key={item.supply_id} className="hover:bg-slate-50">
                      <td className="p-3 font-semibold text-slate-700">{item.size}</td>
                      <td className="p-3 uppercase font-bold text-blue-600">{item.color}</td>
                      <td className="p-3 text-right font-mono text-slate-500">{(item.qty_ordered || 0).toLocaleString()} un</td>
                      <td className="p-3 text-right">
                        <input
                          type="number"
                          min="0"
                          value={editedQuantities[item.supply_id] !== undefined ? editedQuantities[item.supply_id] : ""}
                          onChange={(e) => setEditedQuantities({ ...editedQuantities, [item.supply_id]: Number(e.target.value) })}
                          className="w-full text-right font-mono font-bold border border-emerald-300 bg-emerald-50/20 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-4 rounded-xl text-xs leading-relaxed flex gap-3">
                <Sparkles size={18} className="shrink-0 text-emerald-600" />
                <p>
                  <strong>ATENÇÃO:</strong> Ao confirmar, o saldo físico das pedras no estoque será **acrescido** convertendo as unidades recebidas para quilates (CT) baseado no peso médio de cada pedra, e registrará a data de última pesagem.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 shrink-0">
              <button onClick={() => setShowReceiveModal(false)} className="px-5 py-2 font-bold text-slate-600 hover:bg-slate-200 rounded-lg text-xs">
                Cancelar
              </button>
              <button onClick={handleConfirmReceived} className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-xs shadow-sm">
                Concluir Recebimento
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// Helper para obter a classe de cor da bolinha indicadora da aba
function getColorCircleClass(color) {
  const c = (color || "").toLowerCase().trim();
  switch (c) {
    case "branca": return "bg-white border border-slate-300 shadow-sm";
    case "azul": return "bg-blue-600 shadow-sm";
    case "verde": return "bg-emerald-600 shadow-sm";
    case "vermelho": return "bg-red-600 shadow-sm";
    case "azul claro": return "bg-sky-400 shadow-sm";
    case "rosa turmalina": return "bg-pink-500 shadow-sm";
    case "roxo": return "bg-purple-600 shadow-sm";
    case "verde turmalina": return "bg-teal-600 shadow-sm";
    case "amarelo": return "bg-yellow-400 shadow-sm";
    case "granada-zirconia": return "bg-rose-800 shadow-sm";
    default: return "bg-slate-400 shadow-sm";
  }
}
